import React from 'react';
import DropzoneComponent from 'react-dropzone-component';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PropTypes } from 'prop-types';
import { getStorageSetting } from 'common/storage';
import { connect } from 'react-redux';
import ReactDOMServer from 'react-dom/server';

class FileUpload extends React.Component {
  static propTypes = {
    postUrl: PropTypes.string.isRequired,
    docCategory: PropTypes.string.isRequired,
    docType: PropTypes.string.isRequired,
    linkType: PropTypes.string,
    id: PropTypes.number,
    acceptedFiles: PropTypes.string,
    removeFileAfterUpload: PropTypes.bool,
    defaultMessage: PropTypes.string,
    onFileQueued: PropTypes.func,
    onFileRemoved: PropTypes.func,
    onFileUploadCancel: PropTypes.func,
    onFileUploadSuccess: PropTypes.func,
    onFileUploadError: PropTypes.func,
    uploadFileSettings: PropTypes.shape(
      {
        uploadTimeout: PropTypes.number,
        maxUploadFiles: PropTypes.number,
        maxFileSize: PropTypes.number,
        maxParallelUploads: PropTypes.number
      }
    )
  };

  static defaultProps = {
    defaultMessage: 'Drop files here to upload',
    linkType: 'form',
    id: null,
    removeFileAfterUpload: false,
    onFileQueued: null,
    onFileRemoved: null,
    onFileUploadCancel: null,
    onFileUploadSuccess: null,
    onFileUploadError: null,
    acceptedFiles: null,
    uploadFileSettings: null
  };

  static css(el, styles) {
    const mergedStyles = Object.keys(styles) != null && Object.keys(styles)
      .map(k => k != null && styles[k]).join(',');
    el.setAttribute('style', mergedStyles);
  }

  constructor(props) {
    super(props);

    const { uploadFileSettings } = this.props;

    this.state = {
      timeout: uploadFileSettings != null ? uploadFileSettings.uploadTimeout : 60000,
      maxUploadFiles: uploadFileSettings != null ? uploadFileSettings.maxUploadFiles : 1000,
      maxFilesize: uploadFileSettings != null ? uploadFileSettings.maxFileSize : 5,
      maxParellelUploads: uploadFileSettings != null ? uploadFileSettings.maxParallelUploads : 2
    };

    this.initCallback = this.initCallback.bind(this);
    this.onTimeout = this.onTimeout.bind(this);
  }

  onAddedFile(file) {
    const { onFileQueued } = this.props;

    if (file.accepted === true) {
      if (onFileQueued) {
        onFileQueued(file);
      }
    }
  }

  onRemovedFile(file) {
    const { onFileRemoved } = this.props;

    if (onFileRemoved) {
      onFileRemoved(file);
    }
  }

  onCancel(file) {
    const { onFileUploadCancel } = this.props;

    if (file.accepted === true && file.status === 'canceled') {
      if (onFileUploadCancel) {
        onFileUploadCancel(file);
      }
    }
  }

  onSuccess(file) {
    const { removeFileAfterUpload } = this.props;

    if (removeFileAfterUpload) {
      this.dropzone.removeFile(file);
    }
  }

  onComplete(file) {
    const { onFileUploadSuccess, onFileUploadError } = this.props;

    if (file.accepted === true && file.status === 'success') {
      if (file.xhr && file.xhr.response === '[]') {
        if (onFileUploadError) {
          onFileUploadError(file, { code: 400, message: 'There was problem uploading the document.' });
        }
      }

      if (onFileUploadSuccess) {
        onFileUploadSuccess(file, JSON.parse(file.xhr.response));
      }
    }
  }

  onError(file, response) {
    const { onFileUploadError } = this.props;

    if (file.status === 'error' && (file.accepted === false || (file.accepted === true && response))) {
      if (onFileUploadError) {
        onFileUploadError(file, response.error ? response.error : { code: 400, message: 'There was problem uploading the document.' });
      }
      if (this.dropzone) {
        this.dropzone.removeFile(file);
      }
    }
  }

  onSending(file, xhr, formData) {
    const timeoutHandler = this.onTimeout;

    xhr.ontimeout = function (e) {
      timeoutHandler(file, xhr, formData, e);
    };
  }

  onTimeout(file, xhr, formData, e) {
    const { onFileUploadError } = this.props;

    if (onFileUploadError) {
      onFileUploadError(file, { code: 504, message: `Sorry, there was problem uploading file ${ file.name }` });
    }

    if (this.dropzone) {
      this.dropzone.removeFile(file);
    }
  }

  initCallback(dropzone) {
    this.dropzone = dropzone;

    dropzone.on('success', (file) => {
      const els = document.querySelector('.dz-success-mark span');
      if (els) {
        FileUpload.css(document.querySelector('.dz-success-mark span'), { color: 'green' });
        FileUpload.css(document.querySelector('.dz-error-mark'), { display: 'none' });
      }
    });
    dropzone.on('error', (file) => {
      const els = document.querySelector('.dz-error-mark span');
      if (els) {
        FileUpload.css(document.querySelector('.dz-error-mark span'), { color: 'red' });
        FileUpload.css(document.querySelector('.dz-success-mark'), { display: 'none' });
      }
    });
  }

  render() {
    const { postUrl, docCategory, docType, linkType, id, acceptedFiles, defaultMessage } = this.props;
    const { timeout, maxUploadFiles, maxFilesize, maxParellelUploads } = this.state;

    const componentConfig = {
      postUrl
    };
    const selectedClient = getStorageSetting('selectedClient');
    const headers = { Authorization: null };
    if (selectedClient != null) {
      headers['TT-CLIENT'] = selectedClient.key;
    }
    const djsConfig = {
      withCredentials: true,
      addRemoveLinks: true,
      autoProcessQueue: true,
      maxFiles: maxUploadFiles > 0 ? maxUploadFiles : null,
      timeout,
      acceptedFiles,
      maxFilesize,
      parallelUploads: maxParellelUploads,
      dictDefaultMessage: defaultMessage,
      method: 'put',
      params: {
        category: docCategory,
        type: docType
      },
      previewTemplate: ReactDOMServer.renderToStaticMarkup(
        <div className="dz-preview dz-file-preview">
          <div className="dz-details">
            <div className="dz-filename"><span data-dz-name="true" /></div>
          </div>
          <div className="dz-progress"><div className="dz-upload" data-dz-uploadprogress="true" /></div>
          <div className="dz-success-mark"><span>&#x2714;</span></div>
          <div className="dz-error-mark"><span>&#x2718;</span></div>
          <div className="dz-error-message"><span data-dz-errormessage="true" /></div>
        </div>
      ),
      headers,
    };

    if (id) {
      djsConfig.params.linkType = linkType;
      djsConfig.params.id = id;
    }

    const eventHandlers = {
      init: this.initCallback,
      addedfile: file => this.onAddedFile(file),
      removedfile: file => this.onRemovedFile(file),
      complete: file => this.onComplete(file),
      success: file => this.onSuccess(file),
      canceled: file => this.onCancel(file),
      error: (file, errorMessage) => this.onError(file, errorMessage),
      sending: (file, xhr, formData) => this.onSending(file, xhr, formData)
    };
    return (
      <React.Fragment>
        { docCategory && docType && <DropzoneComponent
          config={ componentConfig }
          eventHandlers={ eventHandlers }
          djsConfig={ djsConfig }
        >
          <div className="dz-default dz-message">
            <div className="row">
              <div className="col">
                <FontAwesomeIcon className="upload-icon" icon="upload" />
                <p>Upload Your Document</p>
              </div>
            </div>
          </div>
        </DropzoneComponent> }
        { (!docCategory || !docType) && <div className="filepicker dropzone dz-disabled">
          <div className="dz-default dz-message">
            <div className="row">
              <div className="col">
                <FontAwesomeIcon className="upload-icon" icon="upload" />
                <p>Upload Your Document</p>
              </div>
            </div>
          </div>
        </div> }
      </React.Fragment>
    );
  }
}

function mapStateToProps(state) {
  const {
    app: {
      onlineConfig
    }
  } = state;

  return {
    uploadFileSettings: onlineConfig && onlineConfig.uploadFileSettings,
  };
}
export default connect(
  mapStateToProps
)(FileUpload);
